import App from "./App.vue"
import util from "@plugins/util"
import axios from "axios"
import router from "./router"
import { createApp } from "vue"
import { dashboardState } from "@state/dashboard-state"
import { connectKeycloak } from "@plugins/keycloak"
import { globalState, updtInterval } from "@state/global-state"
import 'material-icons/iconfont/material-icons.css'
import './assets/css/styles.css'

const vueApp = createApp(App)

// >--< >--< >--< >--< >--< >--< >--< >--<
// >--< >--< STATE  DECLARATIONS >--< >--<
// >--< >--< >--< >--< >--< >--< >--< >--<
//TODO: init default value, will be replaced by process.env
updtInterval.dataInterval = 20 * 60 * 1000
updtInterval.resourceInterval = 24 * 60 * 60 * 1000 
updtInterval.activeDashboardInterval = 60 * 60 * 1000 
/**
 * (asynchronous) function that does the very basic setup
 * before the app can be "run()"
 * mainly handles provisions and configuration calls that
 * are needed for the app to function properly or at all
 */
async function setup() {
  // --- APPLICATION PROVISIONS ---
  // provisions are done first, so the state can be populated by
  // and used to configure plugins
  vueApp.provide("util", util)
  vueApp.provide("globalState", globalState)
  vueApp.provide("updtInterval", updtInterval)
  vueApp.provide("dashboardState", dashboardState)
  // --- --- --- --- --- --- ---

  // --- KEYCLOAK INSTANTIATION ---
  const configResponse = await axios.get(util.middleware() + "/configuration")
  Object.assign(globalState.systemConfig, configResponse.data)

  Object.assign(updtInterval, {
    dataInterval: configResponse.data.dataResourceUpdateInterval.dataInterval,
    resourceInterval: configResponse.data.dataResourceUpdateInterval.resourceInterval,
    activeDashboardInterval : configResponse.data.dataResourceUpdateInterval.activeDashboardInterval
  })

  const keycloakInstance = await connectKeycloak(globalState.systemConfig.keycloak) 

  // --- AXIOS SETUP ---
  // we set up axios to e.g. send headers with every request like the KC token
  configureAxios(keycloakInstance, globalState)
  // --- Route SETUP ---
  configureRouter(router, keycloakInstance, globalState)
}


/**
 * 1. takes a responsive keycloak state to send the current token
 *    with everey request.
 *    this does not work when the reference of the property is passed directly
 *    -> "keycloakResponsiveState.instance.token" as param
 *        since the reactivity is not deep!
 *
 * 2. keeps timers for every request that trigger a loading State if the request
 *    takes too long. Adds a timer for every request, removes a time for every response in time
 *
 * @param {reactive<keycloakState>} keycloakResponsiveState reactive keycloak state object
 */
function configureAxios(
  keycloakInstance,
  globalResponsiveState,
  timeBeforeUiBlockInMs = 2000
) {
  const loadingTimerIds = []

  axios.interceptors.request.use((config) => {
    if (keycloakInstance.authenticated) {
      config.headers["Authorization"] =
        "Bearer " + keycloakInstance.token
    }


    const newTimerId = setTimeout(() => {
      globalResponsiveState.isLoading = true
    }, timeBeforeUiBlockInMs)

    loadingTimerIds.push(newTimerId)

    // pass timeId to config, so that it is available in the response
    config.timerId = newTimerId
    // also pass time of start so wen can check if the timer triggered
    config.timerStart = Date.now()

    return config
  })

  axios.interceptors.response.use(
    (response) => {
      responseTimerHandler(response)
      return response
    },
    (error) => {
      if (error.response) {
        responseTimerHandler(error.response)
      } else {
        responseTimerHandler(error)
      }
      throw error
    }
  )

  // handles timers from resposnes
  const responseTimerHandler = (response) => {
    // cancel the timer
    // and if triggered reset the loading state
    const timerIdToCancel = response.config.timerId
    const timerIndex = loadingTimerIds.indexOf(timerIdToCancel)
    const timerStart = response.config.timerStart
    const now = Date.now()

    const timerTriggered = now - timerStart > timeBeforeUiBlockInMs

    if (timerTriggered) {
      // if the timer was triggered, reset the loading screen
      globalResponsiveState.isLoading = false
    }

    // remove the timer id from the list and clear timeout
    loadingTimerIds.splice(timerIndex, 1)
    clearTimeout(timerIdToCancel)

    // follow the response chain
  }
}

const configureRouter = async(router, keycloakInstance) => {
  router.beforeEach(async(to, from, next) => {
    if (to.meta.requiresAuth) {
      // if to route need auth and keycloak is not authenticated yet, redirect to keycloak login
      if (!keycloakInstance.authenticated) {
        return keycloakInstance.login({ redirectUri: window.location.origin + to.fullPath })
      }
    }
    next()
  })
}

async function run() {
  // --- REGISTER COMPLETE PLUGINS ---
  vueApp.use(router)
  // --- --- --- --- --- --- ---

  // --- MOUNT THE APP --- (actual app start)
  vueApp.mount("#app")
}
// >--< >--< >--< >--< >--< >--< >--< >--<

// >--< >--< >--< >--< >--< >--< >--< >--<
// >--< APP START  PROMISE CHAIN >--< >--<
// >--< >--< >--< >--< >--< >--< >--< >--<
setup()
  .then(() => {
    run()
  })
// >--< >--< >--< >--< >--< >--< >--< >--<
